import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Person,
  SmartToy,
  AttachFile,
  Image as ImageIcon,
  Description,
} from '@mui/icons-material';
import { ChatMessage as ChatMessageType } from '../../types';
import { formatDateTime } from '../../utils/formatting';
import ReactMarkdown from 'react-markdown';

interface ChatMessageProps {
  message: ChatMessageType;
  enableTypewriter?: boolean;
  typingDelta?: string;
}

// Memoized assistant message content to prevent expensive ReactMarkdown re-renders
const AssistantMessageContent = React.memo<{ 
  displayedContent: string; 
  isTyping: boolean; 
  typingDelta?: string;
}>(
  ({ displayedContent, isTyping, typingDelta = '' }) => {
    // Use useMemo to avoid re-parsing markdown on every render
    // CRITICAL: Only parse markdown on the stable content, not the typing delta
    const markdownContent = useMemo(() => (
      <ReactMarkdown>{displayedContent}</ReactMarkdown>
    ), [displayedContent]);

    return (
      <Box
        sx={{
          '& p': { my: 1 },
          '& ul, & ol': { my: 1, pl: 3 },
          '& li': { my: 0.5 },
          '& code': {
            bgcolor: 'action.hover',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: '0.875em',
          },
          '& pre': {
            bgcolor: 'action.hover',
            p: 1,
            borderRadius: 1,
            overflow: 'auto',
          },
        }}
      >
        {markdownContent}
        {/* Append typing delta as plain text to avoid re-parsing incomplete markdown */}
        {typingDelta && (
          <Box
            component="span"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {typingDelta}
          </Box>
        )}
        {isTyping && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 8,
              height: 16,
              bgcolor: 'text.primary',
              ml: 0.5,
              animation: 'blink 1s infinite',
              '@keyframes blink': {
                '0%, 49%': { opacity: 1 },
                '50%, 100%': { opacity: 0 },
              },
            }}
          />
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if displayed content or typing delta changes
    return (
      prevProps.displayedContent === nextProps.displayedContent &&
      prevProps.isTyping === nextProps.isTyping &&
      prevProps.typingDelta === nextProps.typingDelta
    );
  }
);

AssistantMessageContent.displayName = 'AssistantMessageContent';

export const ChatMessage: React.FC<ChatMessageProps> = React.memo(({ 
  message, 
  enableTypewriter = false,
  typingDelta = ''
}) => {
  const isUser = message.role === 'user';
  const isTypingIndicator = message.content === '...' && message.role === 'assistant';
  const isTyping = typingDelta.length > 0;

  return (
    <Box
      display="flex"
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      mb={2}
    >
      <Box
        display="flex"
        flexDirection={isUser ? 'row-reverse' : 'row'}
        gap={1}
        maxWidth="80%"
      >
        <Avatar
          sx={{
            bgcolor: isUser ? 'primary.main' : 'secondary.main',
            width: 36,
            height: 36,
          }}
        >
          {isUser ? <Person /> : <SmartToy />}
        </Avatar>

        <Box flexGrow={1}>
          <Paper
            sx={{
              p: 2,
              bgcolor: isUser ? 'primary.light' : 'background.paper',
              borderRadius: 2,
            }}
          >
            <Box mb={1}>
              <Typography variant="caption" color="text.secondary">
                {isUser ? 'You' : 'AI Assistant'} • {formatDateTime(message.timestamp)}
              </Typography>
            </Box>

            {isTypingIndicator ? (
              <Box display="flex" gap={0.5} alignItems="center" py={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    animation: 'bounce 1.4s infinite ease-in-out both',
                    animationDelay: '-0.32s',
                    '@keyframes bounce': {
                      '0%, 80%, 100%': { transform: 'scale(0)' },
                      '40%': { transform: 'scale(1)' },
                    },
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    animation: 'bounce 1.4s infinite ease-in-out both',
                    animationDelay: '-0.16s',
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    animation: 'bounce 1.4s infinite ease-in-out both',
                  }}
                />
              </Box>
            ) : message.role === 'assistant' ? (
              <AssistantMessageContent 
                displayedContent={message.content} 
                isTyping={isTyping} 
                typingDelta={typingDelta}
              />
            ) : (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.content}
              </Typography>
            )}

            {/* Only show attachments on user messages, never on AI messages */}
            {message.role === 'user' && message.attachments && (() => {
              console.log('[ChatMessage] Message attachments:', message.attachments);
              const userAttachments = message.attachments.filter(att => {
                // Show if explicitly marked as user attachment
                if (att.source === 'user') return true;
                // Show if no source field but looks like a user attachment (backward compatibility)
                // User attachments are stored in /attachments/ path, slide images in /slides/ path
                if (!att.source && att.s3Key && att.s3Key.includes('/attachments/')) return true;
                return false;
              });
              console.log('[ChatMessage] User attachments after filter:', userAttachments);
              return userAttachments.length > 0;
            })() && (
              <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                {message.attachments
                  .filter(att => {
                    if (att.source === 'user') return true;
                    if (!att.source && att.s3Key && att.s3Key.includes('/attachments/')) return true;
                    return false;
                  })
                  .map((attachment, idx) => {
                    // Determine icon based on attachment type
                    let icon = <AttachFile />;
                    if (attachment.attachmentType === 'image') {
                      icon = <ImageIcon />;
                    } else if (attachment.attachmentType === 'text') {
                      icon = <Description />;
                    }
                    
                    return (
                      <Chip
                        key={idx}
                        icon={icon}
                        label={attachment.fileName}
                        size="small"
                        variant="outlined"
                        color={attachment.attachmentType === 'image' ? 'primary' : 'default'}
                      />
                    );
                  })}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if message content, role, messageId, or typingDelta changes
  return (
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.role === nextProps.message.role &&
    prevProps.message.messageId === nextProps.message.messageId &&
    prevProps.enableTypewriter === nextProps.enableTypewriter &&
    prevProps.typingDelta === nextProps.typingDelta
  );
});

ChatMessage.displayName = 'ChatMessage';

